import React, { useMemo } from "react";
import { Routes, Route, Link, useParams } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Badge,
  Button,
  Input,
  ListGroup,
  ListGroupItem,
  Table
} from "reactstrap";
import SuiteLayout from "../../_suite/SuiteLayout";
import IndustrialDashboard from "../../industrial";
import { Colxx, Separator } from "Components/CustomBootstrap";
import BreadcrumbContainer from "Components/BreadcrumbContainer";
import IntlMessages from "Util/IntlMessages";
import {
  SUITE_INDUSTRIAL_OEE_ROW,
  SUITE_INDUSTRIAL_TELEMETRY_PLACEHOLDER,
  INDUSTRIAL_OEE_BAR_MIN
} from "Constants/reactifyInlineStyles";
import { suiteApi } from "../../../../../api/suiteAdapters";
import { useAsyncResource } from "../../../../../hooks/useAsyncResource";
import { AsyncStateBoundary, SuitePageShell } from "../../_suite/primitives";

function IndustrialOeeBarColumn({ heightPct }: { heightPct: number }) {
  const barStyle = useMemo(
    () => ({
      height: `${heightPct}%`,
      ...INDUSTRIAL_OEE_BAR_MIN
    }),
    [heightPct]
  );
  return (
    <div className="bg-primary flex-grow-1 rounded-top" style={barStyle} />
  );
}

function AssetsHealthPage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.industrial.listAssets(),
    []
  );
  return (
    <SuitePageShell heading={<IntlMessages id="suite.industrial.assets" />}>
      <AsyncStateBoundary loading={loading} error={error}>
        <Row>
          {(data || []).map(a => (
            <Col md="6" xl="4" key={a.id} className="mb-4">
              <Card className="h-100 border-0 shadow-sm">
                <CardBody>
                  <div className="d-flex justify-content-between mb-2">
                    <h5 className="mb-0">{a.name}</h5>
                    <Badge
                      color={a.status === "online" ? "success" : "warning"}
                    >
                      {a.status}
                    </Badge>
                  </div>
                  <p className="small text-muted">{a.site}</p>
                  <div className="bg-light rounded p-2 mb-2 small text-center text-muted">
                    OEE sparkline · {a.oee}%
                  </div>
                  <div
                    className="d-flex align-items-end gap-1"
                    style={SUITE_INDUSTRIAL_OEE_ROW}
                  >
                    {[30, 45, 40, 60, 55, 70, a.oee].map((v, i) => (
                      <IndustrialOeeBarColumn key={i} heightPct={v} />
                    ))}
                  </div>
                  <Button
                    tag={Link}
                    color="primary"
                    size="sm"
                    className="mt-3"
                    to={a.id}
                    relative="path"
                  >
                    Telemetry
                  </Button>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

function AssetTelemetryPage() {
  const { id } = useParams();
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.industrial.getAsset(id),
    [id]
  );
  return (
    <div>
      <Row>
        <Colxx xxs="12">
          <BreadcrumbContainer
            heading={<IntlMessages id="suite.industrial.asset-detail" />}
          />
          <Separator className="mb-4" />
        </Colxx>
      </Row>
      <Button tag={Link} color="light" to=".." relative="path" className="mb-3">
        ← Back
      </Button>
      <AsyncStateBoundary loading={loading} error={error} empty={!data}>
        {data && (
          <Row>
            <Col md="8">
              <Card className="mb-3">
                <CardBody>
                  <h3>{data.name}</h3>
                  <p>
                    {data.site} · {data.status}
                  </p>
                  <div
                    className="bg-dark rounded text-white-50 small d-flex align-items-center justify-content-center"
                    style={SUITE_INDUSTRIAL_TELEMETRY_PLACEHOLDER}
                  >
                    Live telemetry chart (OPC-UA / MQTT)
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col md="4">
              <Card>
                <CardBody>
                  <CardTitle tag="h6">OEE</CardTitle>
                  <p className="display-4">{data.oee}%</p>
                </CardBody>
              </Card>
              <Card className="mt-3">
                <CardBody>
                  <CardTitle tag="h6">Maintenance log</CardTitle>
                  <ul className="list-unstyled small mb-0">
                    {(data.maintenanceLog || []).map((e, i) => (
                      <li key={i} className="mb-2 border-bottom pb-2">
                        <span className="text-muted">{e.at}</span> — {e.note}
                      </li>
                    ))}
                    {(!data.maintenanceLog ||
                      data.maintenanceLog.length === 0) && (
                      <li className="text-muted">No CMMS rows (wire API).</li>
                    )}
                  </ul>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
      </AsyncStateBoundary>
    </div>
  );
}

function AlertsGroupedPage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.industrial.listAlerts(),
    []
  );
  const groups = useMemo(() => {
    const list = data || [];
    return {
      high: list.filter(a => a.severity === "high"),
      medium: list.filter(a => a.severity === "medium"),
      other: list.filter(
        a => a.severity !== "high" && a.severity !== "medium"
      )
    };
  }, [data]);

  type AlertRow = {
    id: string;
    time?: string;
    message?: string;
    severity?: string;
    assetId?: string;
  };

  const Block = ({
    title,
    color,
    items
  }: {
    title: string;
    color: string;
    items: AlertRow[];
  }) => (
    <Col md="4" className="mb-4">
      <h6 className={`text-${color}`}>{title}</h6>
      {items.map((a: AlertRow) => (
        <Card key={a.id} className="mb-2 border-0 shadow-sm">
          <CardBody className="py-2">
            <div className="small text-muted">{a.time}</div>
            <div>{a.message}</div>
            {a.assetId && (
              <Badge color="light" className="text-dark mt-1">
                {a.assetId}
              </Badge>
            )}
          </CardBody>
        </Card>
      ))}
    </Col>
  );

  return (
    <SuitePageShell heading={<IntlMessages id="suite.industrial.alerts" />}>
      <AsyncStateBoundary loading={loading} error={error}>
        <Row>
          <Block title="High" color="danger" items={groups.high} />
          <Block title="Medium" color="warning" items={groups.medium} />
          <Block title="Other" color="secondary" items={groups.other} />
        </Row>
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

function WorkOrderChecklistPage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.industrial.listWorkOrders(),
    []
  );
  return (
    <SuitePageShell heading={<IntlMessages id="suite.industrial.workorders" />}>
      <AsyncStateBoundary loading={loading} error={error}>
        {(data || []).map(w => (
          <Card key={w.id} className="mb-3 border-0 shadow-sm">
            <CardBody className="d-flex justify-content-between flex-wrap">
              <div>
                <h5>{w.id}</h5>
                <p className="mb-0">
                  {w.asset} · {w.type}
                </p>
                <Badge>{w.status}</Badge>
                {w.parts && w.parts.length > 0 && (
                  <ul className="small text-muted mb-0 mt-2 ps-3">
                    {w.parts.map((pt, i) => (
                      <li key={i}>
                        {pt.sku} × {pt.qty}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <Button tag={Link} color="outline-primary" to={w.id} relative="path">
                Checklist
              </Button>
            </CardBody>
          </Card>
        ))}
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

function WorkOrderDetailPage() {
  const { id } = useParams();
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.industrial.getWorkOrder(id),
    [id]
  );
  return (
    <div>
      <Row>
        <Colxx xxs="12">
          <BreadcrumbContainer
            heading={<IntlMessages id="suite.industrial.workorder-detail" />}
          />
          <Separator className="mb-4" />
        </Colxx>
      </Row>
      <Button tag={Link} color="light" to=".." relative="path" className="mb-3">
        ← Back
      </Button>
      <AsyncStateBoundary loading={loading} error={error} empty={!data}>
        {data && (
          <Card>
            <CardBody>
              <h4>{data.id}</h4>
              <p>
                {data.asset} · {data.assignee}
              </p>
              {(data.parts || []).length > 0 && (
                <Table size="sm" bordered className="mb-3">
                  <thead>
                    <tr>
                      <th>Part</th>
                      <th>Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.parts.map((pt, i) => (
                      <tr key={i}>
                        <td>{pt.sku}</td>
                        <td>{pt.qty}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
              <ListGroup className="mb-0">
                <ListGroupItem tag="label" className="d-flex align-items-center gap-2">
                  <Input type="checkbox" defaultChecked readOnly /> Lock out / tag out verified
                </ListGroupItem>
                <ListGroupItem tag="label" className="d-flex align-items-center gap-2">
                  <Input type="checkbox" /> Parts staged
                </ListGroupItem>
                <ListGroupItem tag="label" className="d-flex align-items-center gap-2">
                  <Input type="checkbox" /> QA sign-off
                </ListGroupItem>
              </ListGroup>
            </CardBody>
          </Card>
        )}
      </AsyncStateBoundary>
    </div>
  );
}

export function IndustrialSuiteSection() {
  return (
    <Routes>
      <Route element={<SuiteLayout vertical="industrial" />}>
        <Route index element={<IndustrialDashboard />} />
        <Route path="assets" element={<AssetsHealthPage />} />
        <Route path="assets/:id" element={<AssetTelemetryPage />} />
        <Route path="alerts" element={<AlertsGroupedPage />} />
        <Route path="work-orders" element={<WorkOrderChecklistPage />} />
        <Route path="work-orders/:id" element={<WorkOrderDetailPage />} />
      </Route>
    </Routes>
  );
}
